import { FC } from "react";
import styled from "@emotion/styled";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { observer } from "mobx-react-lite";
import { MDSLink, MDSText, MDSTextVariant, MDSTextWeight, MDSToggle } from "@/mds";
import { APP_CONFIG } from "@/domains/global/config";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";
import { useSettingsNotificationPreferencesPageBehavior } from "@/pages/settings/settings-notification-preferences-page.hooks";
import { NotificationPreference } from "@/store/pages/SettingsEmailPreferencesPageStore/types";

const emailNotificationPreferenceLabels: { [key in NotificationPreference]: string } = {
  EMAIL_WEEKLY_NEWSLETTER: "Weekly tips and updates from Mem",
  EMAIL_USER_SHARE: "Notes shared with me",
};

export const SettingsNotificationPreferencesPage: FC = observer(() => {
  const { pageStore, store } = useAppStore();
  const isDesktop = store.interface.isDesktop;
  const isWindows = store.interface.isWindows;
  const isIntelMacDesktop = store.electron.isIntelMacDesktop;
  const page = pageStore.settingsEmailPreferencesPage;
  const accountNotificationPreferencesData = page.accountNotificationPreferencesData.data;

  const toggleEmailNotificationPreference = async (key: NotificationPreference) => {
    if (!accountNotificationPreferencesData) return;
    const isEnabled = accountNotificationPreferencesData[key];
    await page.updateNotificationPreference(key, !isEnabled);
  };

  const {
    isSaving,
    meetingNotificationEnabled,
    toggleMeetingNotificationPreference,
    calendarNotificationEnabled,
    toggleCalendarNotificationPreference,
  } = useSettingsNotificationPreferencesPageBehavior();

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="General" title="Notifications" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            MEETING NOTIFICATIONS
          </StyledSectionLabel>
          <Selector>
            <MDSText variant={MDSTextVariant.ParagraphS}>
              Remind me to join meetings and take notes as they begin
            </MDSText>
            <MDSToggle
              disabled={!isDesktop || isIntelMacDesktop || isSaving}
              checked={meetingNotificationEnabled}
              onChange={() => {
                void toggleMeetingNotificationPreference();
              }}
            />
          </Selector>
          <Selector>
            <MDSText variant={MDSTextVariant.ParagraphS}>
              Detect unscheduled meetings and remind me to have Mem take notes
            </MDSText>
            <MDSToggle
              disabled={!isDesktop || isIntelMacDesktop || isSaving}
              checked={calendarNotificationEnabled}
              onChange={() => {
                void toggleCalendarNotificationPreference();
              }}
            />
          </Selector>
          {!isDesktop && (
            <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
              Use these features in the{" "}
              <MDSLink
                onPress={() => {
                  windowModule.openInNewTab({
                    url: new URL(isWindows ? APP_CONFIG.URLS.WINDOWS_APP : APP_CONFIG.URLS.MAC_APP),
                  });
                }}
              >
                {isWindows ? "Windows" : "Mac"} app
              </MDSLink>
              .
            </MDSText>
          )}
          {isIntelMacDesktop && (
            <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
              This feature is not currently supported on your device.{" "}
              <MDSLink
                onPress={() => {
                  windowModule.openInNewTab({
                    url: new URL(APP_CONFIG.URLS.FAQ_MEETING_NOTIFICATIONS_NOT_SUPPORTED),
                  });
                }}
              >
                Learn more
              </MDSLink>
            </MDSText>
          )}
        </SettingsIntegrationSection>
        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            EMAIL NOTIFICATIONS
          </StyledSectionLabel>
          {Object.entries(emailNotificationPreferenceLabels).map(([key, label]) => {
            const preference = key as NotificationPreference;
            return (
              <Selector key={key}>
                <MDSText variant={MDSTextVariant.ParagraphS}>{label}</MDSText>
                <MDSToggle
                  checked={accountNotificationPreferencesData?.[preference]}
                  onChange={() => toggleEmailNotificationPreference(preference)}
                  disabled={!accountNotificationPreferencesData}
                />
              </Selector>
            );
          })}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledSectionLabel = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Selector = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;
